import { useEffect, useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';

interface VerseChipProps {
  refText: string | null;
  version?: string | null;
}

export function VerseChip({ refText, version }: VerseChipProps) {
  const [tip, setTip] = useState<string>('Loading…');
  const [open, setOpen] = useState(false);

  // Don't render anything if no verse reference
  if (!refText) {
    return <span className="text-slate-400">—</span>;
  }

  const effectiveVersion = version || 'WEB';

  useEffect(() => {
    if (!open || !refText) return;
    let aborted = false;
    (async () => {
      try {
        const url = `/api/bible/lookup?ref=${encodeURIComponent(refText)}&version=${encodeURIComponent(effectiveVersion)}`;
        const r = await fetch(url);
        const j = await r.json();
        if (aborted) return;
        if (!r.ok) {
          setTip(j?.error || 'Not found');
          return;
        }
        const translation = j?.translation ? ` (${j.translation})` : '';
        const text = j?.verses?.map((v: any) => `${v.verse}. ${v.text}`).join(' ') || '—';
        setTip(`${refText}${translation}\n${text}`);
      } catch (e: any) {
        if (!aborted) setTip('Error loading verse');
      }
    })();
    return () => { aborted = true; };
  }, [open, refText, effectiveVersion]);

  const getAttribution = () => {
    switch (effectiveVersion) {
      case 'EWEBIB': return '© Biblica, CC BY-SA 4.0';
      case 'LSG1910': return 'Public Domain (FR)';
      default: return 'Public Domain (WEB)';
    }
  };

  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger asChild>
          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs bg-muted hover:bg-muted/80 cursor-help">
            {refText}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content 
          side="top" 
          className="max-w-[420px] p-3 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-lg border border-slate-200 dark:border-slate-700 text-sm leading-5"
        >
          <div className="space-y-2">
            <div className="font-medium">{refText} <span className="opacity-70">({effectiveVersion})</span></div>
            <div className="mt-1 whitespace-pre-line">{tip}</div>
            <div className="mt-2 text-xs opacity-60">
              {getAttribution()}
            </div>
          </div>
          <Tooltip.Arrow className="fill-white dark:fill-slate-800" />
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
